
  import { computed, defineComponent, ref, watch } from 'vue';
  import { InputField, RadioButtonField } from '@/ui/components/form';
  import { t } from '@/ui/i18n/i18n';
  import { debounce } from 'lodash';
  import { user } from '@/ui/auth/auth';
  import { OrganizationSize } from '@/entities/onboarding/constants';
  import { get, update, updateOnboardingViewed } from '@/services/api/resources/onboarding/actions';
  import { useTask } from '@/ui/concerns';
  import { Loader } from '@/ui/components/Loader';
  import { OrganizationMeta } from '@/entities/onboarding';

  const options = [
    {
      label: t('new-onboarding.form.company-size.micro'),
      value: OrganizationSize.MICRO,
    },
    {
      label: t('new-onboarding.form.company-size.extra-small'),
      value: OrganizationSize.EXTRA_SMALL,
    },
    {
      label: t('new-onboarding.form.company-size.small'),
      value: OrganizationSize.SMALL,
    },
    {
      label: t('new-onboarding.form.company-size.medium'),
      value: OrganizationSize.MEDIUM,
    },
    {
      label: t('new-onboarding.form.company-size.large'),
      value: OrganizationSize.LARGE,
    },
    {
      label: t('new-onboarding.form.company-size.extra-large'),
      value: OrganizationSize.EXTRA_LARGE,
    },
  ];

  export default defineComponent({
    components: {
      InputField,
      RadioButtonField,
      Loader,
    },
    emits: ['validated'],
    setup(_, context) {
      const organizationName = ref('');
      const organizationSize = ref<OrganizationSize|null>(null);
      const canContinue = computed(() => Boolean(organizationName.value.length > 0 && organizationSize.value));

      const getData = useTask(
        () => get(user.value.organization.id),
        response => {
          organizationSize.value = response.data.organization_size as OrganizationSize;

          context.emit('validated', canContinue.value);
        },
      );

      const debouncedDataUpdate = debounce(() => {
        const params = { name: organizationName.value, organization_size: organizationSize.value } as OrganizationMeta;
        update(user.value.organization.id, params)();
      }, 500);

      watch([organizationName, organizationSize], () => {
        context.emit('validated', canContinue.value);
        debouncedDataUpdate();
      });

      if (!user.value.organization.onboardingViewed) {
        updateOnboardingViewed(user.value.organization.id)();

        user.value.organization.onboardingViewed = true;
      }

      return {
        options,
        canContinue,
        getData,
        organizationName,
        organizationSize,
      };
    },
  });
